import React from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Table, Badge } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { toastr } from 'Helpers/ToastHelper';
import { FlightTicketPropType } from 'PropTypes';
import { DATETIME_FORMAT_WEB } from 'Constants/DateFormatConstants';
import { DateTimeFormater } from 'Helpers/DateTimeHelper';
import { FlightTicketApiService } from 'Services/ApiServices';
import { setLoader } from 'Store/Actions/Loading/LoadingAction';
import F2WCustomLogger from 'Services/F2WCustomLogger';

const AttachementCard = (props) => {
  const { flyToWorkTicketInfo, setRefreshComponent, refreshComponent } = props;
  const dispatch = useDispatch();
  const hiddenFileInput = React.useRef(null);
  const [attachments, setAttachments] = React.useState([]);

  // Load attachments when component mounts or refreshes
  React.useEffect(() => {
    if (!flyToWorkTicketInfo?.id) return;

    dispatch(setLoader(true));
    FlightTicketApiService.listAttachments(flyToWorkTicketInfo.id)
      .then((response) => {
        const data = response?.data || response;
        setAttachments(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        // 404 is expected for new tickets without attachments
        if (error?.response?.status === 404) {
          setAttachments([]);
        } else {
          F2WCustomLogger.error('Error loading attachments', error, { ticketId: flyToWorkTicketInfo.id });
          setAttachments([]);
        }
      })
      .finally(() => {
        dispatch(setLoader(false));
      });
  }, [flyToWorkTicketInfo?.id, refreshComponent, dispatch]);

  // Handle file download
  const handleDownloadAttachement = (attachmentId, attachementName) => {
    FlightTicketApiService.downloadAttachment(flyToWorkTicketInfo.id, attachmentId, attachementName);
  };

  // Handle file deletion
  const handleDeleteAttachement = (attachmentId, attachementName) => {
    toastr.confirm(`Are you sure you want to delete "${attachementName}"?`, {
      onOk: () => {
        dispatch(setLoader(true));
        FlightTicketApiService.deleteAttachment(flyToWorkTicketInfo.id, attachmentId, attachementName)
          .then(() => {
            toastr.success('Success', 'Attachment deleted successfully');
          })
          .catch((error) => {
            F2WCustomLogger.error('Error deleting attachment', error, {
              ticketId: flyToWorkTicketInfo.id,
              attachmentId,
            });
          })
          .finally(() => {
            dispatch(setLoader(false));
            setRefreshComponent((prev) => prev + 1);
          });
      },
    });
  };

  // Trigger file input click
  const handleOnClickUploadFile = () => {
    hiddenFileInput.current.click();
  };

  // Handle file upload
  const handleUploadFile = (file) => {
    if (!file) return;

    dispatch(setLoader(true));
    FlightTicketApiService.uploadAttachment(flyToWorkTicketInfo.id, file)
      .then(() => {
        toastr.success('Success', 'File uploaded successfully');
      })
      .catch((error) => {
        F2WCustomLogger.error('Error uploading file', error, { ticketId: flyToWorkTicketInfo.id, fileName: file.name });
        toastr.error('Error', 'Error while uploading file. Format not supported or file too large');
      })
      .finally(() => {
        dispatch(setLoader(false));
        hiddenFileInput.current.value = null;
        setRefreshComponent((prev) => prev + 1);
      });
  };

  const attachmentCount = attachments?.length || 0;

  return (
    <Card className="mt-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <i className="fa fa-paperclip me-2"></i>
          Attachments
          {attachmentCount > 0 && (
            <Badge bg="primary" className="ms-2">
              {attachmentCount}
            </Badge>
          )}
        </div>
        <Button onClick={handleOnClickUploadFile} variant="primary" size="sm">
          <i className="fa fa-upload me-1"></i>
          Upload File
        </Button>
        <input
          ref={hiddenFileInput}
          onChange={(e) => handleUploadFile(e.target.files[0])}
          type="file"
          name="file"
          id="file"
          hidden
        />
      </Card.Header>
      <Card.Body className="p-0">
        {attachmentCount > 0 ? (
          <Table striped hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Uploaded By</th>
                <th>Created Date</th>
                <th className="text-center" style={{ width: '150px' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {attachments.map((attachement) => (
                <tr key={attachement.id || attachement.name}>
                  <td>
                    <i className="fa fa-file-o me-2 text-muted"></i>
                    {attachement.name}
                  </td>
                  <td>{attachement.createdBy}</td>
                  <td>{DateTimeFormater(attachement.created, null, DATETIME_FORMAT_WEB)}</td>
                  <td className="text-center">
                    <Button
                      variant="outline-success"
                      size="sm"
                      className="me-1"
                      onClick={() => handleDownloadAttachement(attachement.id, attachement.name)}
                      title="Download"
                    >
                      <i className="fa fa-download"></i>
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteAttachement(attachement.id, attachement.name)}
                      title="Delete"
                    >
                      <i className="fa fa-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className="text-center py-5">
            <i className="fa fa-paperclip fa-3x text-muted mb-3"></i>
            <p className="text-muted mb-0">No attachments yet</p>
            <p className="text-muted small">Upload files to get started</p>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

AttachementCard.propTypes = {
  flyToWorkTicketInfo: FlightTicketPropType.isRequired,
  setRefreshComponent: PropTypes.func.isRequired,
  refreshComponent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default AttachementCard;
